import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { bootstrapAdminSchema, type BootstrapAdminInput } from '@mygmao/shared';
import { ShieldCheck, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { api, extractErrorMessage } from '../lib/api';

/**
 * US-AUTH-05 — Page de bootstrap du super-administrateur plateforme.
 *
 * Caractéristiques :
 *  - Route cachée (`/my-secret-admin`), non listée dans la navigation.
 *  - Hors layout applicatif (pas de sidebar/header — pas encore connecté).
 *  - POST /platform/bootstrap-admin.
 *  - Une fois utilisée, le backend renvoie 409 sur tout nouvel appel.
 */
export function BootstrapAdminPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<BootstrapAdminInput>({
    resolver: zodResolver(bootstrapAdminSchema),
  });

  async function onSubmit(values: BootstrapAdminInput) {
    setErrorMessage(null);
    try {
      await api.post('/platform/bootstrap-admin', values);
      setStatus('success');
    } catch (err) {
      setStatus('error');
      setErrorMessage(extractErrorMessage(err, 'Création impossible'));
    }
  }

  // Une fois le super-admin créé, on bascule sur l'écran de confirmation puis
  // on redirige automatiquement vers `/login` (3 s) pour la première connexion.
  useEffect(() => {
    if (status !== 'success') return;
    const t = setTimeout(() => navigate('/login', { replace: true }), 3000);
    return () => clearTimeout(t);
  }, [status, navigate]);

  if (status === 'success') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8 text-center">
          <div className="mx-auto w-12 h-12 rounded-full bg-green-50 flex items-center justify-center mb-4">
            <CheckCircle2 className="w-6 h-6 text-green-600" />
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">Super-administrateur créé</h1>
          <p className="text-sm text-gray-600 mb-6">Redirection vers la page de connexion…</p>
          <button
            onClick={() => navigate('/login', { replace: true })}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600"
          >
            Aller à la connexion maintenant
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="bg-white rounded-xl shadow-sm border border-gray-200 max-w-md w-full p-8"
      >
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary-50 flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">Bootstrap super-admin</h1>
            <p className="text-xs text-gray-500">À utiliser une seule fois</p>
          </div>
        </div>

        {status === 'error' && errorMessage && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{errorMessage}</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label htmlFor="first_name" className="block text-sm font-medium text-gray-700 mb-2">
              Prénom <span className="text-red-500">*</span>
            </label>
            <input
              id="first_name"
              type="text"
              autoComplete="given-name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              {...register('first_name')}
            />
            {errors.first_name && (
              <p className="text-red-500 text-xs mt-1">{errors.first_name.message}</p>
            )}
          </div>
          <div>
            <label htmlFor="last_name" className="block text-sm font-medium text-gray-700 mb-2">
              Nom <span className="text-red-500">*</span>
            </label>
            <input
              id="last_name"
              type="text"
              autoComplete="family-name"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              {...register('last_name')}
            />
            {errors.last_name && (
              <p className="text-red-500 text-xs mt-1">{errors.last_name.message}</p>
            )}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            Email <span className="text-red-500">*</span>
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            {...register('email')}
          />
          {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
        </div>

        <div className="mb-4">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
            Mot de passe <span className="text-red-500">*</span>
          </label>
          <input
            id="password"
            type="password"
            autoComplete="new-password"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
            {...register('password')}
          />
          {errors.password ? (
            <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>
          ) : (
            <p className="text-gray-400 text-xs mt-1">
              8+ caractères, majuscule, minuscule, chiffre, caractère spécial
            </p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Création en cours…
            </>
          ) : (
            'Créer le super-administrateur'
          )}
        </button>

        <p className="text-xs text-gray-400 text-center mt-4">
          Cette route devient inopérante après le 1<sup>er</sup> super-admin créé.
        </p>
      </form>
    </div>
  );
}
